import {
  InlineLoading,
  InlineNotification,
  Button,
  SkeletonText,
} from '@carbon/react';
import { Information } from '@carbon/react/icons';
import { useAtom, useAtomValue } from 'jotai';
import { useState, useEffect } from 'react';
import {
  useReconcilerAtom,
  useReconcilerDefaultsAtom,
  ReconcilerDefaultsResponse,
} from './atoms';
import { ReconcilerForm } from './reconciler-form';
import { ReconcilerSummary } from './reconciler-summary';
import { ReconcilerTable } from './reconciler-table';
import { MethodologyModal } from './methodology-modal';
import style from './reconciler.module.scss';

export const TitleChainReconciler = () => {
  const defaultsAtom = useReconcilerDefaultsAtom(
    '/title_validator/reconciler/defaults'
  );
  const [defaults] = useAtom(defaultsAtom);

  if (defaults.loading) {
    return (
      <div className={style.container}>
        <div className={style.header}>
          <div className={style.headerContent}>
            <div>
              <h2>Title Validator Reconciler</h2>
              <SkeletonText className={style.skeletonSubtitle} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (defaults.data && 'error' in defaults.data) {
    return (
      <InlineNotification
        kind="error"
        title="Error loading reconciler configuration"
        lowContrast
      />
    );
  }

  if (!defaults.data) {
    return (
      <InlineNotification
        kind="error"
        title="Failed to load reconciler configuration"
        lowContrast
      />
    );
  }

  return <TitleChainReconcilerContent defaults={defaults.data} />;
};

const TitleChainReconcilerContent = ({
  defaults,
}: {
  defaults: ReconcilerDefaultsResponse;
}) => {
  const [entityId, setEntityId] = useState<number | null>(null);
  const [documentId, setDocumentId] = useState<number | null>(null);
  const [validationId, setValidationId] = useState<string | null>(null);
  const [overlapThreshold, setOverlapThreshold] = useState<number>(
    defaults.shapeInclusionThreshold
  );
  const [shapeMatchThreshold, setShapeMatchThreshold] = useState<number>(
    defaults.shapeSimilarityThreshold
  );
  const [tolerance, setTolerance] = useState<number>(
    defaults.interestDifferenceThreshold
  );
  const [showAllEntityShapes, setShowAllEntityShapes] = useState(false);
  const [documentRef, setDocumentRef] = useState<string | null>(null);
  const [methodologyOpen, setMethodologyOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = (
    newEntityId: number,
    newDocumentId: number,
    newValidationId?: string | null,
    newOverlapThreshold?: number,
    newTolerance?: number,
    newShapeMatchThreshold?: number
  ) => {
    setEntityId(newEntityId);
    setDocumentId(newDocumentId);
    setValidationId(newValidationId ?? null);
    setOverlapThreshold(
      newOverlapThreshold ?? defaults.shapeInclusionThreshold
    );
    setTolerance(newTolerance ?? defaults.interestDifferenceThreshold);
    setShapeMatchThreshold(
      newShapeMatchThreshold ?? defaults.shapeSimilarityThreshold
    );
    // Reset to default (filtered view) when form is submitted with new entity/document
    setShowAllEntityShapes(false);
  };

  const shouldFetch = entityId !== null && documentId !== null;

  return (
    <div className={style.container}>
      {/* Preload fonts containing special unicode math symbols (∩, ∪) to prevent FOUT on first render */}
      <span className={style.fontPreloader} aria-hidden="true">
        ∩∪
      </span>
      <div className={style.header}>
        <div className={style.headerContent}>
          <div>
            <h2>Title Validator Reconciler</h2>
            <p className={style.description}>
              Compare Title Validator output with existing Entity Title Assets
            </p>
          </div>
          <Button
            kind="ghost"
            size="sm"
            renderIcon={Information}
            onClick={() => setMethodologyOpen(true)}
            className={style.methodologyButton}
          >
            How it works
          </Button>
        </div>
      </div>
      <ReconcilerForm
        onSubmit={handleSubmit}
        loading={loading}
        documentRef={documentRef}
        defaults={defaults}
      />
      {shouldFetch && (
        <ReconcilerResults
          entityId={entityId}
          documentId={documentId}
          validationId={validationId}
          overlapThreshold={overlapThreshold}
          tolerance={tolerance}
          shapeMatchThreshold={shapeMatchThreshold}
          showAllEntityShapes={showAllEntityShapes}
          onShowAllEntityShapesChange={setShowAllEntityShapes}
          onResultsLoaded={setDocumentRef}
          onLoadingChange={setLoading}
        />
      )}
      <MethodologyModal
        open={methodologyOpen}
        onClose={() => setMethodologyOpen(false)}
        defaults={defaults}
      />
    </div>
  );
};

function ReconcilerResults({
  entityId,
  documentId,
  validationId,
  overlapThreshold,
  tolerance,
  shapeMatchThreshold,
  showAllEntityShapes,
  onShowAllEntityShapesChange,
  onResultsLoaded,
  onLoadingChange,
}: {
  entityId: number;
  documentId: number;
  validationId: string | null;
  overlapThreshold: number;
  tolerance: number;
  shapeMatchThreshold: number;
  showAllEntityShapes: boolean;
  onShowAllEntityShapesChange: (showAll: boolean) => void;
  onResultsLoaded: (documentRef: string) => void;
  onLoadingChange: (loading: boolean) => void;
}) {
  const reconcilerAtom = useReconcilerAtom(
    entityId,
    documentId,
    validationId,
    overlapThreshold,
    tolerance,
    shapeMatchThreshold
  );
  const reconcilerData = useAtomValue(reconcilerAtom);

  const loading = reconcilerData.loading;
  const error = reconcilerData.data && 'error' in reconcilerData.data;
  const errorMessage =
    error && reconcilerData.data && 'error' in reconcilerData.data
      ? reconcilerData.data.error
      : null;
  const result =
    reconcilerData.data && !('error' in reconcilerData.data)
      ? reconcilerData.data
      : null;

  // Notify parent when loading state changes
  useEffect(() => {
    onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  // Notify parent when results load with documentRef
  useEffect(() => {
    if (result?.documentRef) {
      onResultsLoaded(result.documentRef);
    }
  }, [result, onResultsLoaded]);

  if (loading) {
    return (
      <div className={style.loading}>
        <InlineLoading description="Reconciling title chain data..." />
      </div>
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        title="Error loading reconciliation data"
        subtitle={errorMessage || undefined}
        lowContrast
      />
    );
  }

  if (result) {
    return (
      <>
        <ReconcilerSummary
          result={result}
          showAllEntityShapes={showAllEntityShapes}
          onShowAllEntityShapesChange={onShowAllEntityShapesChange}
        />
        <ReconcilerTable
          comparisonResults={result.comparisonResults}
          burdenedLeaseComparisons={result.burdenedLeaseComparisons || []}
          tolerance={result.tolerance}
          workspaces={result.workspaces}
        />
      </>
    );
  }

  return null;
}
